// Shape of the release manifests published to Cloud Storage by the mobile and
// desktop release workflows, and the pure logic that turns one into the version
// groups the download page renders.
export type ReleaseFile = {
  label?: string;
  platform?: string;
  architecture?: string;
  type?: string;
  version?: string;
  url?: string;
  downloadUrl?: string;
  objectPath?: string;
  size?: number;
  sha256?: string;
};
export type HistoryEntry = {
  version?: string;
  publishedAt?: string;
  files?: ReleaseFile[];
};
export type Release = {
  version?: string;
  buildNumber?: number | string;
  publishedAt?: string;
  downloadUrl?: string;
  size?: number;
  sha256?: string;
  releaseNotes?: string[] | string;
  files?: ReleaseFile[];
  history?: HistoryEntry[];
};
export type VersionGroup = {
  version: string;
  publishedAt?: string;
  files: ReleaseFile[];
  /** True when the manifest still serves these files, i.e. they are what a new
   * installation gets. Normally exactly one group is current; a release that
   * built only one platform leaves the other platform's older version current
   * too. */
  current: boolean;
};
export const formatSize = (value?: number) =>
  value ? `${(value / 1024 / 1024).toFixed(1)} MB` : 'Size unavailable';
export const formatDate = (value?: string) => {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toLocaleDateString();
};
export const fileUrl = (file: ReleaseFile) => file.downloadUrl || file.url;
export const fileKey = (file: ReleaseFile) => file.objectPath || fileUrl(file) || file.label || '';
// Package descriptions are keyed on the manifest's `type`, which the release
// workflows derive from the file extension. Releases no longer build an MSI, but
// earlier versions' MSIs stay listed under Previous versions until pruning
// removes them, and those must still say they do not update themselves.
const PACKAGES: Record<string, { name: string; note?: string; platform?: string; rank: number }> = {
  installer: {
    name: 'Windows installer (.exe)',
    note: 'Recommended. Installs and updates itself in place.',
    platform: 'windows',
    rank: 0,
  },
  msi: {
    name: 'Windows MSI package (.msi)',
    note: 'For managed deployment. Does not update itself — install a newer MSI to upgrade.',
    platform: 'windows',
    rank: 1,
  },
  dmg: { name: 'macOS disk image (.dmg)', platform: 'macos', rank: 2 },
  zip: {
    name: 'macOS archive (.zip)',
    note: 'Used by the in-app updater; download it only if the disk image will not open.',
    platform: 'macos',
    rank: 3,
  },
  apk: { name: 'Android package (.apk)', platform: 'android', rank: 4 },
};
const PLATFORMS: Record<string, string> = {
  windows: 'Windows',
  macos: 'macOS',
  android: 'Android',
};
export const describe = (file: ReleaseFile) => PACKAGES[file.type || ''];
export const packageName = (file: ReleaseFile) =>
  describe(file)?.name ||
  file.label ||
  `${file.platform || ''} ${file.type || ''}`.trim() ||
  'Download';
export const platformOf = (file: ReleaseFile) =>
  file.platform || describe(file)?.platform || 'other';
export const platformName = (file: ReleaseFile) => {
  const key = platformOf(file);
  const label = PLATFORMS[key] || key.charAt(0).toUpperCase() + key.slice(1);
  return file.architecture ? `${label} (${file.architecture})` : label;
};
export const rank = (file: ReleaseFile) => describe(file)?.rank ?? 99;
export const sortFiles = (files: ReleaseFile[]) => [...files].sort((a, b) => rank(a) - rank(b));
// Newest first, by numeric version segments so 1.0.10 outranks 1.0.9. A
// prerelease suffix (1.1.0-beta.2) sorts below the same release version.
export const compareVersions = (a: string, b: string) => {
  const parts = (value: string) => {
    const [core, pre] = value.split('-', 2);
    return { core: core.split('.').map(n => Number.parseInt(n, 10) || 0), pre };
  };
  const left = parts(a);
  const right = parts(b);
  for (let i = 0; i < Math.max(left.core.length, right.core.length); i += 1) {
    const diff = (right.core[i] || 0) - (left.core[i] || 0);
    if (diff) return diff;
  }
  if (left.pre === right.pre) return 0;
  if (!left.pre) return -1;
  if (!right.pre) return 1;
  return left.pre < right.pre ? 1 : -1;
};
// One group per version. The manifest's own files are the current release, but
// a run that built only one platform carries the other platform's files forward
// unchanged, so a file's own `version` wins over the manifest's when present.
export const groupVersions = (release?: Release): VersionGroup[] => {
  if (!release) return [];
  const latestVersion = release.version || 'Latest';
  const served: ReleaseFile[] = release.files?.length
    ? release.files
    : release.downloadUrl
      ? [
          {
            label: 'Download',
            type: 'apk',
            version: release.version,
            downloadUrl: release.downloadUrl,
            size: release.size,
            sha256: release.sha256,
          },
        ]
      : [];
  const groups = new Map<string, VersionGroup>();
  const add = (version: string, file: ReleaseFile, publishedAt?: string, current = false) => {
    const group = groups.get(version) || { version, publishedAt, files: [], current };
    group.current = group.current || current;
    group.publishedAt = group.publishedAt || publishedAt;
    if (!group.files.some(existing => fileKey(existing) === fileKey(file))) group.files.push(file);
    groups.set(version, group);
  };
  served.forEach(file => {
    const version = file.version || latestVersion;
    add(version, file, version === latestVersion ? release.publishedAt : undefined, true);
  });
  release.history?.forEach(entry => {
    entry.files?.forEach(file => {
      const version = file.version || entry.version;
      if (!version) return;
      add(version, file, entry.publishedAt);
    });
  });
  return [...groups.values()]
    .map(group => ({ ...group, files: sortFiles(group.files) }))
    .sort((a, b) => {
      if (a.current !== b.current) return a.current ? -1 : 1;
      return compareVersions(a.version, b.version);
    });
};
export const groupByPlatform = (files: ReleaseFile[]) => {
  const platforms = new Map<string, ReleaseFile[]>();
  files.forEach(file => {
    const key = platformName(file);
    platforms.set(key, [...(platforms.get(key) || []), file]);
  });
  return [...platforms.entries()];
};
